// TrackRidesComponent.jsx
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftCircle, Info, RefreshCw, Truck, XCircle } from 'lucide-react'; // Import necessary Lucide icons
import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../components/ui/button.js'; // Assuming this path
import { driverApi } from '../Driver/driverApi.js'; // Import driverApi for schedule data
import { getProfilePhoto } from '../OrgManagement/employeeApiService.js'; // Import getProfilePhoto for profile images
import { SearchBar } from '../ui'; // Import SearchBar component
import LiveRideCard from './LiveRideCard.js'; // Import the LiveRideCard component

// Utility function to combine Tailwind CSS classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism style definition (re-used from Dashboard)
const glassmorphism = 'bg-white/20 backdrop-blur-md rounded-xl border border-white/10 shadow-lg';

/**
 * TrackRidesComponent allows users to view active rides and dispatch new ones.
 * It now functions as a side panel, displaying a list of rides or a detailed view of a single ride.
 *
 * @param {Object[]} liveRides - An array of current live ride objects to display.
 * @param {Object[]} officeLocations - An array of office location objects to populate site dropdowns.
 * @param {Object[]} employees - An array of employee objects with their status information.
 * @param {function(Object): void} onDispatchNewRide - Callback function to dispatch a new ride.
 * @param {function(): void} onClose - Callback function to close the entire panel.
 * @param {function(string|null): void} onSelectRideForMap - Callback to inform parent (Dashboard) about selected ride for map display.
 * @param {string|null} selectedRideIdForMap - The ID of the ride currently selected for map display (from Dashboard).
 * @param {function(): void} onShowEmployeeInfo - Callback to trigger the employee info table.
 * @param {boolean} hideCloseButton - Whether to hide the close button (for dedicated dashboard view).
 * @param {function(): void} onRefresh - Callback function to refresh the live rides list.
 * @param {function(Object): void} onDriverClick - Callback when a driver/employee is clicked. Passes {rideId, userId, driverName}.
 * @param {Object|null} selectedDriver - Currently selected driver object with rideId, userId, driverName.
 */
const TrackRidesComponent = ({ liveRides, officeLocations, employees = [], onDispatchNewRide, onClose, onSelectRideForMap, selectedRideIdForMap, onShowEmployeeInfo, hideCloseButton = false, onRefresh, onDriverClick, selectedDriver }) => {
    // State for new ride form fields
    const [startSiteId, setStartSiteId] = useState('');
    const [endSiteId, setEndSiteId] = useState('');
    const [material, setMaterial] = useState('');
    const [driver, setDriver] = useState('');
    const [vehicle, setVehicle] = useState('');
    const [speed, setSpeed] = useState(''); // Added speed state
    const [battery, setBattery] = useState(''); // Added battery state

    // State to manage which ride is currently expanded in the detailed view within this panel
    const [expandedRideId, setExpandedRideId] = useState(null);

    // State for search functionality
    const [searchTerm, setSearchTerm] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState([]);

    // State for convoy details
    const [convoyDetails, setConvoyDetails] = useState([]);
    const [loadingConvoy, setLoadingConvoy] = useState(false);
    
    // State for all drivers
    const [allDrivers, setAllDrivers] = useState([]);
    const [loadingDrivers, setLoadingDrivers] = useState(false);


    // Synchronize internal expandedRideId with external selectedRideIdForMap
    useEffect(() => {
        setExpandedRideId(selectedRideIdForMap);
    }, [selectedRideIdForMap]);

    // Fetch all drivers on component mount
    useEffect(() => {
        const fetchDrivers = async () => {
            try {
                setLoadingDrivers(true);
                const driversData = await driverApi.fetchAllDrivers();
                setAllDrivers(driversData || []);
            } catch (error) {
                console.error('Error fetching drivers:', error);
                setAllDrivers([]);
            } finally {
                setLoadingDrivers(false);
            }
        };

        fetchDrivers();
    }, []);

    // Fetch convoy details when a ride is expanded
    useEffect(() => {
        const fetchConvoyDetails = async () => {
            if (!expandedRideId) {
                setConvoyDetails([]);
                return;
            }

            const currentExpandedRide = liveRides.find(ride => ride.id === expandedRideId);
            if (!currentExpandedRide) {
                setConvoyDetails([]);
                return;
            }

            // Extract date from ride_start_timestamp
            let rideDate;
            if (currentExpandedRide.ride_start_timestamp) {
                const dateObj = new Date(currentExpandedRide.ride_start_timestamp);
                rideDate = dateObj.toISOString().split('T')[0]; // Format: YYYY-MM-DD
            } else {
                // Fallback to today's date if no timestamp
                rideDate = new Date().toISOString().split('T')[0];
            }

            setLoadingConvoy(true);
            try {
                // Fetch schedule data for the ride date
                const scheduleData = await driverApi.fetchDriverSchedulesByDate(rideDate);
                
                // Find the matching ride by ride_id first, then fallback to from_site_id and to_site_id
                let matchingRide = scheduleData.find(ride => ride.ride_id === currentExpandedRide.id);
                
                // Fallback: match by from_site_id and to_site_id if ride_id doesn't match
                if (!matchingRide && currentExpandedRide.from_site_id && currentExpandedRide.to_site_id) {
                    matchingRide = scheduleData.find(ride => 
                        ride.from_site_id === currentExpandedRide.from_site_id && 
                        ride.to_site_id === currentExpandedRide.to_site_id
                    );
                }
                
                if (matchingRide && matchingRide.convoy_details && Array.isArray(matchingRide.convoy_details)) {
                    // Map convoy_details to include employee information with driver names and profile images
                    const convoyWithEmployeeInfo = await Promise.all(
                        matchingRide.convoy_details.map(async (convoyMember) => {
                            // Try to find matching driver first (by user_id or employee_id)
                            const driver = allDrivers.find(
                                d => d.user_id === convoyMember.user_id || 
                                     d.employee_id === convoyMember.user_id ||
                                     d.user_id === convoyMember.employee_id ||
                                     d.employee_id === convoyMember.employee_id
                            );
                            
                            // Fallback to employees array if driver not found
                            const employee = !driver 
                                ? employees.find(emp => emp.id === convoyMember.user_id)
                                : null;
                            
                            // Determine name - prioritize driver name
                            let name = driver?.user_name || 
                                       driver?.name || 
                                       employee?.name || 
                                       convoyMember.user_id || 
                                       'Unknown';
                            
                            // Try to fetch profile image
                            let profileImage = null;
                            try {
                                const userId = driver?.user_id || driver?.employee_id || convoyMember.user_id;
                                if (userId) {
                                    profileImage = await getProfilePhoto(userId);
                                }
                            } catch (photoError) {
                                console.warn(`Failed to fetch profile photo for ${name}:`, photoError);
                            }
                            
                            // Use profile image, fallback to employee image, then placeholder
                            const imageUrl = profileImage || 
                                           driver?.profile_image || 
                                           employee?.image || 
                                           `https://placehold.co/48x48/aabbcc/ffffff?text=${name.charAt(0).toUpperCase()}`;
                            
                            // Fetch driver status using getDriverStatus API
                            let driverStatus = 'Unknown';
                            let driverStatusData = null;
                            try {
                                const userId = convoyMember.user_id || driver?.user_id || driver?.employee_id;
                                const rideId = matchingRide.ride_id || currentExpandedRide.id;
                                if (userId && rideId) {
                                    driverStatusData = await driverApi.getDriverStatus(rideId, userId);
                                    driverStatus = driverStatusData?.status || 'Unknown';
                                }
                            } catch (statusError) {
                                console.warn(`Failed to fetch driver status for ${name}:`, statusError);
                                // Fallback to employee status if API fails
                                driverStatus = employee?.status || driver?.status || 'Unknown';
                            }
                            
                            return {
                                user_id: convoyMember.user_id || driver?.user_id || driver?.employee_id,
                                employee_id: driver?.employee_id || convoyMember.employee_id,
                                vehicle_id: convoyMember.vehicle_id,
                                docking_station: convoyMember.docking_station || null,
                                material: convoyMember.material || null,
                                name: name,
                                image: imageUrl,
                                role: driver?.role_name || driver?.spec_role || employee?.role || 'Driver',
                                status: driverStatus,
                                statusData: driverStatusData, // Store full status data for future use
                            };
                        })
                    );
                    
                    setConvoyDetails(convoyWithEmployeeInfo);
                } else {
                    setConvoyDetails([]);
                }
            } catch (error) {
                console.error('Error fetching convoy details:', error);
                setConvoyDetails([]);
            } finally {
                setLoadingConvoy(false);
            }
        };

        fetchConvoyDetails();
    }, [expandedRideId, liveRides, employees, allDrivers]);

    // Handle search functionality
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Generate search suggestions based on "From" and "To" site names
        if (value.length > 0) {
            const suggestions = new Set();
            liveRides.forEach(ride => {
                if (ride.startSiteName && ride.startSiteName.toLowerCase().includes(value.toLowerCase())) {
                    suggestions.add(ride.startSiteName);
                }
                if (ride.endSiteName && ride.endSiteName.toLowerCase().includes(value.toLowerCase())) {
                    suggestions.add(ride.endSiteName);
                }
            });
            setSearchSuggestions(Array.from(suggestions));
        } else {
            setSearchSuggestions([]);
        }
    };

    const handleSearchSubmit = (value) => {
        console.log('Search submitted:', value);
        // Search is handled through filtering, so we just need to update the term
        setSearchTerm(value);
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchTerm(suggestion);
        setSearchSuggestions([]);
    };

    // Filter rides based on search term
    const filteredRides = useMemo(() => {
        if (!searchTerm.trim()) {
            return liveRides;
        }
        const searchLower = searchTerm.toLowerCase();
        return liveRides.filter(ride => 
            (ride.startSiteName && ride.startSiteName.toLowerCase().includes(searchLower)) ||
            (ride.endSiteName && ride.endSiteName.toLowerCase().includes(searchLower))
        );
    }, [liveRides, searchTerm]);

    // Helper function to get status color based on driver status
    const getStatusColor = (status) => {
        if (!status) {
            return { dot: 'bg-gray-500', text: 'text-gray-600' };
        }
        
        const statusLower = status.toLowerCase();
        if (statusLower === 'on duty') {
            return { dot: 'bg-green-700', text: 'text-green-700' };
        } else if (statusLower === 'duty not started') {
            return { dot: 'bg-yellow-500', text: 'text-yellow-600' };
        } else if (statusLower === 'duty ended') {
            return { dot: 'bg-gray-500', text: 'text-gray-600' };
        } else {
            return { dot: 'bg-gray-500', text: 'text-gray-600' };
        }
    };

    // Helper function to format time range from timestamps
    const formatTimeRange = (startTimestamp, endTimestamp) => {
        try {
            if (!startTimestamp || !endTimestamp) {
                return 'Time not available';
            }
            
            // Parse ISO timestamp strings
            const startDate = new Date(startTimestamp);
            const endDate = new Date(endTimestamp);
            
            // Extract hours and minutes, pad with zero if needed
            const startHours = String(startDate.getHours()).padStart(2, '0');
            const startMinutes = String(startDate.getMinutes()).padStart(2, '0');
            const endHours = String(endDate.getHours()).padStart(2, '0');
            const endMinutes = String(endDate.getMinutes()).padStart(2, '0');
            
            return `${startHours}:${startMinutes} to ${endHours}:${endMinutes}`;
        } catch (error) {
            console.error('Error formatting time range:', error);
            return 'Time not available';
        }
    };


    /**
     * Handles the dispatching of a new ride.
     * Validates input, creates a new ride object, and calls the onDispatchNewRide callback.
     */
    const handleDispatchRide = () => {
        // Basic validation for form fields
        if (!startSiteId || !endSiteId || !material || !driver || !vehicle) {
            console.error('Please fill all fields to dispatch a ride.');
            return;
        }

        // Find the full site objects based on selected IDs
        const startSite = officeLocations.find(site => site.id === startSiteId);
        const endSite = officeLocations.find(site => site.id === endSiteId);

        if (!startSite || !endSite) {
            console.error('Invalid start or end site selected.');
            return;
        }

        // Create a new ride object with necessary details for map display
        const newRide = {
            id: `ride${Date.now()}`, // Unique ID for the new ride
            status: 'not started',    // Initial status
            vehicle,
            driver,
            destination: endSite.address, // Destination address
            material,
            speed: parseFloat(speed) || null,    // Include speed, default to null if not a valid number
            battery: parseFloat(battery) || null,  // Include battery, default to null if not a valid number
            startLat: startSite.lat, // Start latitude for map route
            startLng: startSite.lng, // Start longitude for map route
            endLat: endSite.lat,     // End latitude for map route
            endLng: endSite.lng,     // End longitude for map route
            startSiteName: startSite.name, // Start site name for display
            endSiteName: endSite.name,     // End site name for display
            // Add a mock route for new rides if needed, or leave empty
            route: [
                { lat: startSite.lat, lng: startSite.lng },
                { lat: endSite.lat, lng: endSite.lng }
            ]
        };

        // Call the parent component's function to add the new ride
        onDispatchNewRide(newRide);

        // Clear the form fields after dispatching
        setStartSiteId('');
        setEndSiteId('');
        setMaterial('');
        setDriver('');
        setVehicle('');
        setSpeed('');
        setBattery('');
        // Do not close the entire panel, just reset the form
    };

    // Function to handle selecting a ride for detailed view
    const handleSelectRide = (rideId) => {
        const newId = expandedRideId === rideId ? null : rideId;
        setExpandedRideId(newId);
        onSelectRideForMap(newId); // Inform Dashboard to update map
    };

    const currentExpandedRide = liveRides.find(ride => ride.id === expandedRideId);

    return (
        <motion.div
            initial={{ opacity: 0, x: '100%' }} // Start from right
            animate={{ opacity: 1, x: 0 }} // Slide to position
            exit={{ opacity: 0, x: '100%' }} // Exit to right
            transition={{ duration: 0.3 }}
            // Main container styling using default card background from card.js
            className="w-96 self-stretch relative bg-white/10 backdrop-blur-md border border-white/10 rounded-xl shadow-lg overflow-hidden"
            style={{ height: '787px' }} // Fixed height for consistency with other panels
        >
            {/* Header Section */}
            <div className="w-96 h-20 left-0 top-0 absolute bg-neutral-50/40 rounded-xl shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px] flex items-center justify-between px-6">
                {expandedRideId ? (
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => handleSelectRide(null)} // Go back to summary view
                            className="w-8 h-8 flex items-center justify-center bg-neutral-50/50 rounded-full shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] border border-white/0 backdrop-blur-[30px]"
                            aria-label="Back to all rides"
                        >
                            <ArrowLeftCircle className="w-5 h-5 text-black" />
                        </button>
                        <div className="text-center text-black text-xl font-bold font-['Figtree'] leading-normal">From and To</div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        {/* <PlayCircle className="w-6 h-6 text-green-700" /> */}
                        <div className="text-black text-xl font-bold font-['Figtree'] leading-normal">Track Live Rides</div>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    {onRefresh && (
                        <button
                            onClick={onRefresh}
                            className="w-8 h-8 flex items-center justify-center bg-blue-500/10 rounded-full shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] border border-white/0 backdrop-blur-[30px] hover:bg-blue-500/20 transition-colors"
                            aria-label="Refresh live rides"
                            title="Refresh"
                        >
                            <RefreshCw className="w-5 h-5 text-blue-600" />
                        </button>
                    )}
                    {!hideCloseButton && (
                        <button
                            onClick={onClose} // Close the entire Track Rides panel
                            className="w-8 h-8 flex items-center justify-center bg-neutral-50/50 rounded-full shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] border border-white/0 backdrop-blur-[30px]"
                            aria-label="Close track rides panel"
                        >
                            <XCircle className="w-5 h-5 text-black" />
                        </button>
                    )}
                </div>
            </div>

            {/* Content Area - Conditional Rendering */}
            {/* Added a fixed height to the container that holds the rides and dispatch form,
                and applied overflow-y-auto to enable the scrollbar. */}
            <div className="w-96 left-[16px] top-[96px] absolute flex flex-col justify-start items-start gap-1 overflow-y-auto custom-scrollbar pr-6" style={{ height: 'calc(100% - 128px)' }}>
                {expandedRideId && currentExpandedRide ? (
                    // Display single expanded ride details using LiveRideCard
                    <>
                        <LiveRideCard
                            ride={currentExpandedRide}
                            onClick={() => {}} // No action on click when in expanded view
                            onShowEmployeeInfo={onShowEmployeeInfo}
                            isSelected={true} // Always true when in expanded view
                        />

                        {/* Employee Section */}
                        <div className="self-stretch relative">
                            <div className="w-full h-10 left-0 top-0 absolute inline-flex justify-between items-center">
                                <div className="justify-center text-black text-lg font-bold font-['Figtree'] leading-snug">Employee</div>
                                <div className="flex justify-start items-center gap-2.5">
                                    <button
                                        onClick={() => {
                                            // Pass the current ride's convoy_details to display all drivers
                                            // Use convoyDetails state which has processed employee info
                                            if (convoyDetails && convoyDetails.length > 0) {
                                                // Transform convoyDetails into table format
                                                const driverData = convoyDetails.map((convoyMember, index) => ({
                                                    serialNumber: index + 1,
                                                    vehicleId: convoyMember.vehicle_id || 'N/A',
                                                    userId: convoyMember.user_id || 'N/A',
                                                    driverName: convoyMember.name || 'N/A',
                                                    rideId: currentExpandedRide.ride_id || currentExpandedRide.id,
                                                    fromSiteId: currentExpandedRide.from_site_id,
                                                    toSiteId: currentExpandedRide.to_site_id,
                                                    docking_station: convoyMember.docking_station || null,
                                                    material: convoyMember.material || currentExpandedRide.material || 'N/A',
                                                    speed: currentExpandedRide.speed || null,
                                                    battery: currentExpandedRide.battery || null,
                                                    status: convoyMember.status || currentExpandedRide.status || 'in progress',
                                                    endSiteName: currentExpandedRide.endSiteName || 'N/A',
                                                }));
                                                onShowEmployeeInfo(driverData);
                                            } else if (currentExpandedRide && currentExpandedRide.convoy_details) {
                                                // Fallback to raw convoy_details if convoyDetails state is not available
                                                const driverData = currentExpandedRide.convoy_details.map((convoyMember, index) => ({
                                                    serialNumber: index + 1,
                                                    vehicleId: convoyMember.vehicle_id || convoyMember.vehicle?.id || 'N/A',
                                                    userId: convoyMember.user_id || convoyMember.driver?.id || 'N/A',
                                                    driverName: convoyMember.name || convoyMember.driver?.name || convoyMember.user_id || 'N/A',
                                                    rideId: currentExpandedRide.ride_id || currentExpandedRide.id,
                                                    fromSiteId: currentExpandedRide.from_site_id,
                                                    toSiteId: currentExpandedRide.to_site_id,
                                                    docking_station: convoyMember.docking_station || null,
                                                    material: convoyMember.material || currentExpandedRide.material || 'N/A',
                                                    speed: currentExpandedRide.speed || null,
                                                    battery: currentExpandedRide.battery || null,
                                                    status: currentExpandedRide.status || 'in progress',
                                                    endSiteName: currentExpandedRide.endSiteName || 'N/A',
                                                }));
                                                onShowEmployeeInfo(driverData);
                                            }
                                        }}
                                        className="p-2 bg-neutral-50/50 rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-300 flex justify-start items-center gap-2.5 overflow-hidden"
                                        title="View Ride Information"
                                    >
                                        <Info className="w-5 h-5 text-black" />
                                    </button>
                                </div>
                            </div>
                            <div className="w-full left-0 top-[52px] absolute inline-flex flex-col justify-start items-center gap-3 overflow-hidden">
                                {loadingConvoy ? (
                                    <div className="self-stretch pb-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed">
                                        Loading convoy details...
                                    </div>
                                ) : convoyDetails.length > 0 ? (
                                    convoyDetails.map((convoyMember, index) => {
                                        const isSelectedDriver = selectedDriver && 
                                          selectedDriver.userId === convoyMember.user_id &&
                                          selectedDriver.rideId === currentExpandedRide?.id;
                                        
                                        const isDutyNotStarted = convoyMember.status && convoyMember.status.toLowerCase() === 'duty not started';
                                        
                                        return (
                                        <div key={convoyMember.user_id || index} className="self-stretch justify-between items-center">
                                            <div 
                                                className={combineClasses(
                                                    "flex-1 px-3 py-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex justify-between items-center transition-all",
                                                    isSelectedDriver ? "ring-2 ring-blue-500 bg-blue-50" : "",
                                                    isDutyNotStarted ? "cursor-not-allowed opacity-75" : "cursor-pointer hover:bg-gray-50"
                                                )}
                                                onClick={() => {
                                                    // Don't allow clicking if duty not started
                                                    if (isDutyNotStarted) {
                                                        return;
                                                    }
                                                    
                                                    if (onDriverClick && currentExpandedRide && convoyMember.user_id) {
                                                        // Toggle selection: if already selected, deselect; otherwise select
                                                        if (isSelectedDriver) {
                                                            onDriverClick(null);
                                                        } else {
                                                            // Get driver name with fallback pattern similar to fetchAndDisplayConvoyDriverMarkers
                                                            const driverName = convoyMember.driver?.name || 
                                                                             convoyMember.name || 
                                                                             'Driver';
                                                            onDriverClick({
                                                                rideId: currentExpandedRide.id,
                                                                userId: convoyMember.user_id,
                                                                driverName: driverName,
                                                            });
                                                        }
                                                    }
                                                }}
                                                title={isDutyNotStarted ? "Duty not started - location unavailable" : (isSelectedDriver ? "Click to hide driver path" : "Click to view driver traveled path")}
                                            >
                                                <div className="flex justify-start items-center gap-3 flex-1">
                                                    <img 
                                                        className="w-12 h-12 relative rounded-lg object-cover" 
                                                        src={convoyMember.image} 
                                                        alt={convoyMember.name}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            const fallbackText = convoyMember.name?.charAt(0).toUpperCase() || convoyMember.user_id?.charAt(0).toUpperCase() || '?';
                                                            e.target.src = `https://placehold.co/48x48/aabbcc/ffffff?text=${fallbackText}`;
                                                        }}
                                                    />
                                                    <div className="flex-1 inline-flex flex-col justify-start items-start">
                                                        <div className="self-stretch justify-start text-black text-base font-normal font-['Open_Sans'] leading-relaxed">
                                                            {convoyMember.name}
                                                            {isSelectedDriver && (
                                                                <span className="ml-2 text-xs text-blue-600 font-semibold">(Tracking)</span>
                                                            )}
                                                        </div>
                                                        {/* <div className="self-stretch justify-start text-black text-sm font-normal font-['Open_Sans'] leading-relaxed text-gray-600">
                                                            User ID: {convoyMember.user_id}
                                                        </div> */}
                                                        <div className="self-stretch justify-start text-black text-sm font-normal font-['Open_Sans'] leading-relaxed text-gray-600">
                                                            Vehicle ID: {convoyMember.vehicle_id}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex justify-end items-center gap-1.5 ml-4">
                                                    {(() => {
                                                        const statusColors = getStatusColor(convoyMember.status);
                                                        return (
                                                            <>
                                                                <div className={combineClasses("w-3 h-3 rounded-full", statusColors.dot)} />
                                                                <div className={combineClasses("text-right justify-center text-sm font-normal font-['Open_Sans'] leading-relaxed whitespace-nowrap", statusColors.text)}>
                                                                    {convoyMember.status}
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })
                                ) : (
                                    <div className="self-stretch pb-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed text-gray-500">
                                        No convoy details available
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    // Display list of all live rides + Dispatch New Ride form
                    <>
                        {/* Search Bar Section - Added above Active Rides */}
                        <div className="mb-4 w-full">
                            <SearchBar
                                placeholder="Search by From or To site..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onSearch={handleSearchSubmit}
                                suggestions={searchSuggestions}
                                onSuggestionClick={handleSuggestionClick}
                                className="w-full"
                            />
                        </div>
                        {/* <h3 className="text-xl font-semibold text-gray-700 mb-1">Active Rides</h3> */}
                        {filteredRides.length > 0 ? (
                            <div className="space-y-4 w-full">
                                <AnimatePresence>
                                    {filteredRides.map(ride => (
                                        <motion.div
                                            key={ride.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 20 }}
                                            transition={{ duration: 0.5 }}
                                            className="self-stretch p-3 rounded-xl outline outline-1 outline-offset-[-1px] outline-white flex flex-col justify-start items-start gap-2.5 cursor-pointer hover:opacity-90 transition-opacity"
                                            style={{
                                                background: 'radial-gradient(152.09% 184.58% at 136.24% -51.30%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%)',
                                            }}
                                            onClick={() => handleSelectRide(ride.id)}
                                        >
                                            <div className="self-stretch inline-flex justify-between items-center">
                                                <div className="justify-center text-black text-lg font-bold font-['Figtree'] leading-snug">
                                                    {formatTimeRange(ride.ride_start_timestamp, ride.ride_end_timestamp)}
                                                </div>
                                                <div className="p-1 flex justify-start items-center gap-2 overflow-hidden">
                                                    <Truck className="w-6 h-6 text-black" />
                                                    {/* "Track Ride" button beside truck icon */}
                                                    <Button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent card click when clicking button
                                                            handleSelectRide(ride.id);
                                                        }}
                                                        className={combineClasses("px-3 py-1 text-sm rounded-full !text-white", selectedRideIdForMap === ride.id ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-600 hover:bg-blue-700")}
                                                    >
                                                        {selectedRideIdForMap === ride.id ? "Tracking" : "Track Ride"}
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="self-stretch p-3 bg-neutral-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 inline-flex flex-col justify-start items-start gap-1">
                                                <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">From: {ride.startSiteName}</div>
                                                {ride.fromSupervisorName && (
                                                    <div className="justify-start text-black text-sm font-normal font-['Open_Sans'] leading-relaxed text-gray-600">
                                                        Assigned Employee: {ride.fromSupervisorName}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="self-stretch p-3 bg-neutral-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 inline-flex flex-col justify-start items-start gap-1">
                                                <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">To: {ride.endSiteName}</div>
                                                {ride.toSupervisorName && (
                                                    <div className="justify-start text-black text-sm font-normal font-['Open_Sans'] leading-relaxed text-gray-600">
                                                        Assigned Employee: {ride.toSupervisorName}
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <p className="text-gray-600 w-full">
                                {searchTerm.trim() ? `No rides found matching "${searchTerm}".` : 'No active rides.'}
                            </p>
                        )}

                        {/* Section for Dispatching New Ride */}
                        {/* <div className="mt-6 w-full"> */}
                            {/* <h3 className="text-xl font-semibold text-gray-700 mb-4">Dispatch New Ride</h3> */}
                            {/* <div className="space-y-4"> */}
                                {/* Start Site Selection */}
                                {/* <div>
                                    <label htmlFor="startSite" className="block text-gray-700 text-sm font-medium mb-1">Start Site</label>
                                    <select
                                        id="startSite"
                                        value={startSiteId}
                                        onChange={(e) => setStartSiteId(e.target.value)}
                                        className={combineClasses(glassmorphism, 'w-full bg-blue-100/10 p-2 rounded-md text-gray-900')}
                                        required
                                    >
                                        <option value="">Select Start Site</option>
                                        {officeLocations.map(site => (
                                            <option key={site.id} value={site.id}>{site.name}</option>
                                        ))}
                                    </select>
                                </div> */}
                                {/* End Site Selection */}
                                {/* <div>
                                    <label htmlFor="endSite" className="block text-gray-700 text-sm font-medium mb-1">End Site</label>
                                    <select
                                        id="endSite"
                                        value={endSiteId}
                                        onChange={(e) => setEndSiteId(e.target.value)}
                                        className={combineClasses(glassmorphism, 'w-full bg-blue-100/10 p-2 rounded-md text-gray-900')}
                                        required
                                    >
                                        <option value="">Select End Site</option>
                                        {officeLocations.map(site => (
                                            <option key={site.id} value={site.id}>{site.name}</option>
                                        ))}
                                    </select>
                                </div> */}
                                {/* Material Input */}
                                {/* <div>
                                    <label htmlFor="material" className="block text-gray-700 text-sm font-medium mb-1">Material</label>
                                    <Input
                                        id="material"
                                        type="text"
                                        value={material}
                                        onChange={(e) => setMaterial(e.target.value)}
                                        className={combineClasses(glassmorphism, 'w-full placeholder:text-gray-500')}
                                        required
                                    />
                                </div> */}
                                {/* Driver Input */}
                                {/* <div>
                                    <label htmlFor="driver" className="block text-gray-700 text-sm font-medium mb-1">Driver</label>
                                    <Input
                                        id="driver"
                                        type="text"
                                        value={driver}
                                        onChange={(e) => setDriver(e.target.value)}
                                        className={combineClasses(glassmorphism, 'w-full placeholder:text-gray-500')}
                                        required
                                    />
                                </div> */}
                                {/* Vehicle Input */}
                                {/* <div>
                                    <label htmlFor="vehicle" className="block text-gray-700 text-sm font-medium mb-1">Vehicle</label>
                                    <Input
                                        id="vehicle"
                                        type="text"
                                        value={vehicle}
                                        onChange={(e) => setVehicle(e.target.value)}
                                        className={combineClasses(glassmorphism, 'w-full placeholder:text-gray-500')}
                                        required
                                    />
                                </div> */}
                                {/* Speed Input */}
                                {/* <div>
                                    <label htmlFor="speed" className="block text-gray-700 text-sm font-medium mb-1">Speed (km/h)</label>
                                    <Input
                                        id="speed"
                                        type="number"
                                        value={speed}
                                        onChange={(e) => setSpeed(e.target.value)}
                                        className={combineClasses(glassmorphism, 'w-full placeholder:text-gray-500')}
                                        placeholder="e.g., 60"
                                    />
                                </div> */}
                                {/* Battery Input */}
                                {/* <div>
                                    <label htmlFor="battery" className="block text-gray-700 text-sm font-medium mb-1">Battery (%)</label>
                                    <Input
                                        id="battery"
                                        type="number"
                                        value={battery}
                                        onChange={(e) => setBattery(e.target.value)}
                                        className={combineClasses(glassmorphism, 'w-full placeholder:text-gray-500')}
                                        placeholder="e.g., 85"
                                        min="0"
                                        max="100"
                                    />
                                </div> */}
                                {/* Dispatch Ride Button */}
                                {/* <Button
                                    onClick={handleDispatchRide}
                                    className="bg-green-700/80 text-white hover:bg-green-700 w-full"
                                >
                                    <PlusCircle className="w-5 h-5 mr-2" />
                                    Dispatch New Ride
                                </Button> */}
                            {/* </div> */}
                        {/* </div> */}
                    </>
                )}
            </div>
        </motion.div>
    );
};

export default TrackRidesComponent;
